/**
 * IndexedDB Utility
 * Manages video cache and poster targets in IndexedDB
 * Storage limit: 10-20MB for videos
 */

const DB_NAME = 'sr_ar_db';
const DB_VERSION = 1;
const VIDEO_STORE = 'videos';
const TARGET_STORE = 'targets';

let dbInstance = null;

/**
 * Open IndexedDB database
 * @returns {Promise<IDBDatabase>} - Database instance
 */
export async function openDB() {
  if (dbInstance) {
    return dbInstance;
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error(`Failed to open IndexedDB: ${request.error}`));
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create video store
      if (!db.objectStoreNames.contains(VIDEO_STORE)) {
        const videoStore = db.createObjectStore(VIDEO_STORE, { keyPath: 'videoId' });
        videoStore.createIndex('timestamp', 'timestamp', { unique: false });
        videoStore.createIndex('size', 'size', { unique: false });
      }

      // Create target store
      if (!db.objectStoreNames.contains(TARGET_STORE)) {
        const targetStore = db.createObjectStore(TARGET_STORE, { keyPath: 'posterId' });
        targetStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
}

/**
 * Save video to IndexedDB
 * @param {string} videoId - Video ID
 * @param {Blob} blob - Video blob data
 * @param {object} metadata - Video metadata
 * @returns {Promise<boolean>} - True if saved successfully
 */
export async function saveVideo(videoId, blob, metadata = {}) {
  try {
    const db = await openDB();
    const transaction = db.transaction([VIDEO_STORE], 'readwrite');
    const store = transaction.objectStore(VIDEO_STORE);

    const videoData = {
      videoId,
      blob,
      size: blob.size,
      timestamp: Date.now(),
      ...metadata,
    };

    return new Promise((resolve, reject) => {
      const request = store.put(videoData);

      request.onsuccess = () => {
        console.log(`Video ${videoId} saved to IndexedDB (${blob.size} bytes)`);
        resolve(true);
      };

      request.onerror = () => {
        reject(new Error(`Failed to save video: ${request.error}`));
      };
    });
  } catch (error) {
    console.error('Failed to save video to IndexedDB:', error);
    return false;
  }
}

/**
 * Get video from IndexedDB
 * @param {string} videoId - Video ID
 * @returns {Promise<object|null>} - Video data or null if not found
 */
export async function getVideo(videoId) {
  try {
    const db = await openDB();
    const transaction = db.transaction([VIDEO_STORE], 'readonly');
    const store = transaction.objectStore(VIDEO_STORE);

    return new Promise((resolve, reject) => {
      const request = store.get(videoId);

      request.onsuccess = () => {
        if (request.result) {
          console.log(`Video ${videoId} retrieved from IndexedDB`);
        }
        resolve(request.result || null);
      };

      request.onerror = () => {
        reject(new Error(`Failed to get video: ${request.error}`));
      };
    });
  } catch (error) {
    console.error('Failed to get video from IndexedDB:', error);
    return null;
  }
}

/**
 * Clear videos from IndexedDB
 * @param {string[]} videoIds - Optional array of video IDs to clear. If empty, clears all.
 * @returns {Promise<number>} - Number of videos cleared
 */
export async function clearVideos(videoIds = []) {
  try {
    const db = await openDB();
    const transaction = db.transaction([VIDEO_STORE], 'readwrite');
    const store = transaction.objectStore(VIDEO_STORE);

    if (videoIds.length === 0) {
      // Clear all videos
      return new Promise((resolve, reject) => {
        const request = store.clear();

        request.onsuccess = () => {
          console.log('All videos cleared from IndexedDB');
          resolve(0); // Can't count cleared items with clear()
        };

        request.onerror = () => {
          reject(new Error(`Failed to clear videos: ${request.error}`));
        };
      });
    }

    // Clear specific videos
    let count = 0;
    const promises = videoIds.map((videoId) => {
      return new Promise((resolve, reject) => {
        const request = store.delete(videoId);

        request.onsuccess = () => {
          count++;
          resolve();
        };

        request.onerror = () => {
          reject(new Error(`Failed to delete video ${videoId}: ${request.error}`));
        };
      });
    });

    await Promise.all(promises);
    console.log(`${count} videos cleared from IndexedDB`);
    return count;
  } catch (error) {
    console.error('Failed to clear videos from IndexedDB:', error);
    return 0;
  }
}

/**
 * Save image target to IndexedDB
 * @param {string} posterId - Poster ID
 * @param {ArrayBuffer} targetData - MindAR target data
 * @returns {Promise<boolean>} - True if saved successfully
 */
export async function saveTarget(posterId, targetData) {
  try {
    const db = await openDB();
    const transaction = db.transaction([TARGET_STORE], 'readwrite');
    const store = transaction.objectStore(TARGET_STORE);

    const target = {
      posterId,
      data: targetData,
      timestamp: Date.now(),
    };

    return new Promise((resolve, reject) => {
      const request = store.put(target);

      request.onsuccess = () => {
        console.log(`Target for poster ${posterId} saved to IndexedDB`);
        resolve(true);
      };

      request.onerror = () => {
        reject(new Error(`Failed to save target: ${request.error}`));
      };
    });
  } catch (error) {
    console.error('Failed to save target to IndexedDB:', error);
    return false;
  }
}

/**
 * Get image target from IndexedDB
 * @param {string} posterId - Poster ID
 * @returns {Promise<object|null>} - Target data or null if not found
 */
export async function getTarget(posterId) {
  try {
    const db = await openDB();
    const transaction = db.transaction([TARGET_STORE], 'readonly');
    const store = transaction.objectStore(TARGET_STORE);

    return new Promise((resolve, reject) => {
      const request = store.get(posterId);

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        reject(new Error(`Failed to get target: ${request.error}`));
      };
    });
  } catch (error) {
    console.error('Failed to get target from IndexedDB:', error);
    return null;
  }
}
